import React, { useState, useCallback, useMemo, memo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import ConstraintChip from "./ConstraintChip.jsx";
import FilterSidebar from "../sidebar-components/FilterSidebar.jsx";

// Memoize static icons
const filtersIcon = icon({ name: "filters", family: "sharp", style: "light" });
const lockIcon = icon({ name: "lock", family: "sharp", style: "solid" });

/**
 * Get summary text for a filter's selected values
 * @param {Array} selectedValues - Array of selected value strings
 * @returns {string} Summary text
 */
const getFilterSummary = (selectedValues) => {
	if (!selectedValues || selectedValues.length === 0) {
		return "All";
	}
	if (selectedValues.length === 1) {
		return selectedValues[0];
	}
	return `${selectedValues.length} selected`;
};

/**
 * Format time period for display
 * @param {Object} timePeriod - Object with start_date and end_date
 * @returns {string} Formatted date range
 */
const formatTimePeriod = (timePeriod) => {
	if (!timePeriod?.start_date || !timePeriod?.end_date) {
		return "";
	}
	const startDate = new Date(timePeriod.start_date);
	const endDate = new Date(timePeriod.end_date);
	const options = { month: 'short', day: 'numeric' };
	const startStr = startDate.toLocaleDateString('en-US', options);
	const endStr = endDate.toLocaleDateString('en-US', options);
	return `${startStr} - ${endStr}`;
};

/**
 * FilterSection - A labeled section of filter chips
 */
const FilterSection = memo(({
	label,
	filters,
	isLocked,
	showLockIcon,
	onChipClick,
	onRemoveFilter,
	emptyText,
	timePeriod,
	onRemoveTimePeriod,
	hasViewFilters,
}) => {
	const hasFilters = filters?.length > 0;
	const hasTimePeriod = timePeriod?.start_date && timePeriod?.end_date;
	const isEmpty = !hasFilters && !hasTimePeriod;

	// Don't return null if there are view filters (we need to show the label/lock icon)
	if (isEmpty && !emptyText && !hasViewFilters) {
		return null;
	}

	return (
		<div className="flex flex-wrap items-center gap-2">
			{/* Section label - show when there are filters, time period, or view filters */}
			{(hasFilters || hasTimePeriod || hasViewFilters) && (
				<span className="text-text-2 text-xs font-medium whitespace-nowrap cursor-default">
					{label}
				</span>
			)}
			{/* Lock icon - show when there are filters, time period, or view filters */}
			{showLockIcon && (hasFilters || hasTimePeriod || hasViewFilters) && (
				<FontAwesomeIcon
					icon={lockIcon}
					className="text-text-3 text-xs"
					title="Report filters are locked in view mode"
				/>
			)}
			
			{/* Time period chip */}
			{hasTimePeriod && (
				<ConstraintChip
					category="Time"
					summary={formatTimePeriod(timePeriod)}
					selectedValues={[formatTimePeriod(timePeriod)]}
					onClick={() => {}} // Time period editing happens in the header
					onRemove={!isLocked ? onRemoveTimePeriod : undefined}
					isLocked={isLocked}
					isTimePeriod={true}
				/>
			)}
			
			{/* Filter chips */}
			{hasFilters && filters.map((filter) => (
				<ConstraintChip
					key={filter.category}
					category={filter.categoryDisplay || filter.category}
					summary={getFilterSummary(filter.selectedValues)}
					selectedValues={filter.selectedValues}
					onClick={() => onChipClick(filter.category)}
					onRemove={() => onRemoveFilter(filter.category)}
					isLocked={isLocked}
				/>
			))}
			{/* Empty text when no filters and no time period and no view filters */}
			{!hasFilters && !hasTimePeriod && !hasViewFilters && emptyText && (
				<div className="flex flex-col text-text-2 text-sm py-1 pl-2">
					<span>No filters applied</span>
					<span className="text-text-3 text-xs mt-0.5">Showing all available data</span>
				</div>
			)}
		</div>
	);
});

FilterSection.displayName = 'FilterSection';

FilterSection.propTypes = {
	label: PropTypes.string.isRequired,
	filters: PropTypes.array.isRequired,
	isLocked: PropTypes.bool,
	showLockIcon: PropTypes.bool,
	onChipClick: PropTypes.func,
	onRemoveFilter: PropTypes.func,
	emptyText: PropTypes.string,
	timePeriod: PropTypes.shape({
		start_date: PropTypes.string,
		end_date: PropTypes.string,
	}),
	onRemoveTimePeriod: PropTypes.func,
	hasViewFilters: PropTypes.bool,
};

/**
 * ActiveConstraintsBar - Container showing active filter constraints as chips
 * 
 * Supports both report filters (saved with report) and view filters (temporary).
 * In report context, shows two sections with appropriate labels.
 */
export const ActiveConstraintsBar = memo(({
	// New props for report/view filter separation
	reportFilters,
	setReportFilters,
	viewFilters,
	setViewFilters,
	reportTimePeriod,
	onRemoveTimePeriod,
	isReportContext = false,
	showFilterPrompt = false,
	onAddToReport,
	onKeepAsPreview,
	// Legacy props for non-report contexts
	filters,
	setFilters,
	// Common props
	additionalFilters,
	excludeCategories,
	token,
	headerHeight = 140,
	isEditMode = true,
}) => {
	// Track which filter's sidebar is open (by category) and which type
	const [openFilterCategory, setOpenFilterCategory] = useState(null);
	const [openFilterType, setOpenFilterType] = useState(null); // 'report' or 'view'
	const [isClosing, setIsClosing] = useState(false); // Track closing animation state
	const closeTimeoutRef = useRef(null); // Ref to store timeout for cleanup

	// Determine if we're using the new report/view filter props or legacy props
	const isUsingReportViewFilters = isReportContext && reportFilters !== undefined;

	// Safe access to filters
	const safeReportFilters = useMemo(
		() => (Array.isArray(reportFilters) ? reportFilters : []),
		[reportFilters]
	);
	const safeViewFilters = useMemo(
		() => (Array.isArray(viewFilters) ? viewFilters : []),
		[viewFilters]
	);
	const safeLegacyFilters = useMemo(
		() => (Array.isArray(filters) ? filters : []),
		[filters]
	);

	// Get all filters for finding the open one
	const allFilters = useMemo(() => {
		if (isUsingReportViewFilters) {
			return [...safeReportFilters, ...safeViewFilters];
		}
		return safeLegacyFilters;
	}, [isUsingReportViewFilters, safeReportFilters, safeViewFilters, safeLegacyFilters]);

	// Get the currently open filter object
	const openFilter = useMemo(() => {
		if (!openFilterCategory) return null;
		return allFilters.find(f => f.category === openFilterCategory) || null;
	}, [openFilterCategory, allFilters]);

	// Stabilize array props to prevent unnecessary re-renders in FilterSidebar
	// This ensures fetchFilterValues (and the useEffect that depends on it) 
	// stops being recreated on every render and prevents duplicate API calls
	const stableAdditionalFilters = useMemo(() => {
		return Array.isArray(additionalFilters) ? additionalFilters : [];
	}, [additionalFilters]);

	const stableExcludeCategories = useMemo(() => {
		return Array.isArray(excludeCategories) ? excludeCategories : [];
	}, [excludeCategories]);

	// Determine the setter function based on filter type
	const getSetterForType = useCallback((type) => {
		if (type === 'report') return setReportFilters;
		if (type === 'view') return setViewFilters;
		return setFilters;
	}, [setReportFilters, setViewFilters, setFilters]);

	// Handle clicking on a report filter chip
	const handleReportChipClick = useCallback((category) => {
		if (isEditMode) {
			// Clear any pending close timeout
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
				closeTimeoutRef.current = null;
			}
			setIsClosing(false);
			setOpenFilterCategory(category);
			setOpenFilterType('report');
		}
	}, [isEditMode]);

	// Handle clicking on a view filter chip
	const handleViewChipClick = useCallback((category) => {
		// View filters can always be edited
		// Clear any pending close timeout
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setIsClosing(false);
		setOpenFilterCategory(category);
		setOpenFilterType('view');
	}, []);

	// Handle clicking on a legacy filter chip
	const handleLegacyChipClick = useCallback((category) => {
		if (isEditMode) {
			// Clear any pending close timeout
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
				closeTimeoutRef.current = null;
			}
			setIsClosing(false);
			setOpenFilterCategory(category);
			setOpenFilterType('legacy');
		}
	}, [isEditMode]);

	// Handle closing the sidebar with animation delay
	const handleCloseSidebar = useCallback(() => {
		// Clear any existing timeout
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
		}
		setIsClosing(true);
		// Wait for animation to complete (300ms) before unmounting
		closeTimeoutRef.current = setTimeout(() => {
			setOpenFilterCategory(null);
			setOpenFilterType(null);
			setIsClosing(false);
			closeTimeoutRef.current = null;
		}, 300);
	}, []);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		};
	}, []);

	// Handle removing a report filter
	const handleRemoveReportFilter = useCallback((category) => {
		if (setReportFilters) {
			setReportFilters((currentFilters) =>
				currentFilters.filter((f) => f.category !== category)
			);
		}
		setOpenFilterCategory(null);
		setOpenFilterType(null);
	}, [setReportFilters]);

	// Handle removing a view filter
	const handleRemoveViewFilter = useCallback((category) => {
		if (setViewFilters) {
			setViewFilters((currentFilters) =>
				currentFilters.filter((f) => f.category !== category)
			);
		}
		setOpenFilterCategory(null);
		setOpenFilterType(null);
	}, [setViewFilters]);

	// Handle removing a legacy filter
	const handleRemoveLegacyFilter = useCallback((category) => {
		if (setFilters) {
			setFilters((currentFilters) =>
				currentFilters.filter((f) => f.category !== category)
			);
		}
		setOpenFilterCategory(null);
		setOpenFilterType(null);
	}, [setFilters]);

	// Get the appropriate remove handler based on filter type
	const getRemoveHandler = useCallback((type) => {
		if (type === 'report') return handleRemoveReportFilter;
		if (type === 'view') return handleRemoveViewFilter;
		return handleRemoveLegacyFilter;
	}, [handleRemoveReportFilter, handleRemoveViewFilter, handleRemoveLegacyFilter]);

	// Handle updating filter selection
	const handleUpdateFilter = useCallback((category, selectedValues) => {
		const setter = getSetterForType(openFilterType);
		if (setter) {
			setter((currentFilters) =>
				currentFilters.map((f) =>
					f.category === category
						? { ...f, selectedValues }
						: f
				)
			);
		}
	}, [getSetterForType, openFilterType]);

	// Determine what to show
	const hasReportFilters = safeReportFilters.length > 0;
	const hasViewFilters = safeViewFilters.length > 0;
	const hasLegacyFilters = safeLegacyFilters.length > 0;
	const hasReportTimePeriod = reportTimePeriod?.start_date && reportTimePeriod?.end_date;
	const hasAnyContent = hasReportFilters || hasViewFilters || hasLegacyFilters || hasReportTimePeriod;

	// In legacy mode (non-report pages like analyzer), hide bar but keep spacing when no filters
	if (!isUsingReportViewFilters && !hasLegacyFilters) {
		return <div className="py-2" />;
	}

	// In view mode with no content, show minimal space
	if (!isEditMode && !hasAnyContent && !isUsingReportViewFilters) {
		return <div className="mb-4" />;
	}

	// Determine view filter label based on context
	// View filters are the global Zustand filters that persist across pages
	const viewFilterLabel = isEditMode 
		? "View filters (not saved with report)" 
		: "View filters";

	return (
		<>
			<div className="text-left flex flex-col text-text-2 border border-card-bd m-4 mr-6 ml-10 bg-card rounded-card mb-2 min-h-[54px]">
				{/* Report filters section - always shown in report context */}
				{isUsingReportViewFilters && (
					<div className="flex items-center py-2 px-4 border-b border-border last:border-b-0">
						{/* Filter icon */}
						<div className="flex flex-row pr-2">
							<FontAwesomeIcon
								icon={filtersIcon}
								className="text-text-2"
							/>
						</div>
						
						<FilterSection
							label="Report filters"
							filters={safeReportFilters}
							isLocked={!isEditMode}
							showLockIcon={!isEditMode}
							onChipClick={handleReportChipClick}
							onRemoveFilter={handleRemoveReportFilter}
							emptyText={!hasReportFilters && !hasReportTimePeriod && !hasViewFilters ? "show" : null}
							timePeriod={reportTimePeriod}
							onRemoveTimePeriod={onRemoveTimePeriod}
							hasViewFilters={hasViewFilters}
						/>
					</div>
				)}

				{/* View filters section - only in report context when there are view filters */}
				{isUsingReportViewFilters && hasViewFilters && (
					<div className="flex items-center py-2 px-4">
						{/* Spacer to align with report filters */}
						<div className="flex flex-row pr-2 invisible">
							<FontAwesomeIcon
								icon={filtersIcon}
								className="text-text-2"
							/>
						</div>
						
						<FilterSection
							label={viewFilterLabel}
							filters={safeViewFilters}
							isLocked={false}
							showLockIcon={false}
							onChipClick={handleViewChipClick}
							onRemoveFilter={handleRemoveViewFilter}
						/>
					</div>
				)}

				{/* Legacy mode - single filter section (for non-report pages) */}
				{!isUsingReportViewFilters && (
					<div className="flex items-center py-2 px-4">
						{/* Filter icon */}
						<div className="flex flex-row pr-2">
							<FontAwesomeIcon
								icon={filtersIcon}
								className="text-text-2"
							/>
						</div>

						{/* Filter chips */}
						<div className="flex flex-wrap gap-2">
							{hasLegacyFilters ? (
								safeLegacyFilters.map((filter) => (
									<ConstraintChip
										key={filter.category}
										category={filter.categoryDisplay || filter.category}
										summary={getFilterSummary(filter.selectedValues)}
										selectedValues={filter.selectedValues}
										onClick={() => handleLegacyChipClick(filter.category)}
										onRemove={() => handleRemoveLegacyFilter(filter.category)}
										isLocked={!isEditMode}
									/>
								))
							) : (
								isEditMode && (
									<span className="text-text-2 text-sm py-1">
										No filters added yet
									</span>
								)
							)}
						</div>

						{/* Lock icon - shown when not in edit mode */}
						{!isEditMode && hasLegacyFilters && (
							<div className="flex flex-row pl-2 ml-auto">
								<FontAwesomeIcon
									icon={lockIcon}
									className="text-text-3"
									title="Filters are locked in view mode"
								/>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Filter conversion prompt */}
			{showFilterPrompt && hasViewFilters && (
				<div className="mx-10 mr-6 mb-2 px-4 py-2 bg-primary-soft border border-primary rounded-lg flex items-center justify-between">
					<span className="text-sm text-text">
						Copy view filters to this report?
					</span>
					<div className="flex gap-2">
						<button
							onClick={onAddToReport}
							className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary-hover transition-colors"
						>
							Copy to report
						</button>
						<button
							onClick={onKeepAsPreview}
							className="px-3 py-1 text-sm bg-card text-text-2 border border-border rounded hover:bg-surface-2 transition-colors"
						>
							Keep separate
						</button>
					</div>
				</div>
			)}

			{/* Add view filters to report action bar (when editing with existing report filters) */}
			{isEditMode && hasViewFilters && hasReportFilters && !showFilterPrompt && (
				<div className="mx-10 mr-6 mb-2 px-4 py-2 bg-surface-2 border border-border rounded-lg flex items-center justify-between">
					<span className="text-sm text-text-2">
						View filters are not saved with the report
					</span>
					<button
						onClick={onAddToReport}
						className="px-3 py-1 text-sm bg-text text-text-inverse rounded hover:bg-text-2 transition-colors"
					>
						Copy view filters to report
					</button>
				</div>
			)}

			{/* Filter sidebar - keep mounted during closing animation */}
			{(openFilterCategory || isClosing) && (
				<FilterSidebar
					isOpen={!!openFilterCategory && !isClosing}
					onClose={handleCloseSidebar}
					filter={openFilter}
					onUpdateFilter={handleUpdateFilter}
					onRemoveFilter={getRemoveHandler(openFilterType)}
					token={token}
					additionalFilters={stableAdditionalFilters}
					excludeCategories={stableExcludeCategories}
					headerHeight={headerHeight}
				/>
			)}
		</>
	);
});

ActiveConstraintsBar.displayName = 'ActiveConstraintsBar';

ActiveConstraintsBar.propTypes = {
	// New report/view filter props
	/** Array of report filter objects (saved with report) */
	reportFilters: PropTypes.arrayOf(PropTypes.shape({
		category: PropTypes.string.isRequired,
		categoryDisplay: PropTypes.string,
		selectedValues: PropTypes.arrayOf(PropTypes.string),
	})),
	/** Function to update report filters state */
	setReportFilters: PropTypes.func,
	/** Array of view filter objects (temporary, not saved) */
	viewFilters: PropTypes.arrayOf(PropTypes.shape({
		category: PropTypes.string.isRequired,
		categoryDisplay: PropTypes.string,
		selectedValues: PropTypes.arrayOf(PropTypes.string),
	})),
	/** Function to update view filters state */
	setViewFilters: PropTypes.func,
	/** Report time period object */
	reportTimePeriod: PropTypes.shape({
		start_date: PropTypes.string,
		end_date: PropTypes.string,
	}),
	/** Function to remove time period from report */
	onRemoveTimePeriod: PropTypes.func,
	/** Whether we're in a report context (shows two sections) */
	isReportContext: PropTypes.bool,
	/** Whether to show the filter conversion prompt */
	showFilterPrompt: PropTypes.bool,
	/** Callback to add view filters to report */
	onAddToReport: PropTypes.func,
	/** Callback to dismiss prompt and keep as preview */
	onKeepAsPreview: PropTypes.func,
	// Legacy props
	/** Array of filter objects (legacy single-filter mode) */
	filters: PropTypes.arrayOf(PropTypes.shape({
		category: PropTypes.string.isRequired,
		categoryDisplay: PropTypes.string,
		selectedValues: PropTypes.arrayOf(PropTypes.string),
	})),
	/** Function to update filters state (legacy) */
	setFilters: PropTypes.func,
	// Common props
	/** Additional filters for API requests */
	additionalFilters: PropTypes.array,
	/** Categories to exclude from API requests */
	excludeCategories: PropTypes.array,
	/** Auth token for API requests */
	token: PropTypes.string,
	/** Height of the header in pixels (sidebar starts below this) */
	headerHeight: PropTypes.number,
	/** Whether the constraint bar is in edit mode (false = locked) */
	isEditMode: PropTypes.bool,
};

export default ActiveConstraintsBar;
